import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreVertical, Edit2, Trash2 } from 'lucide-react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { Card, Button, BottomSheet, SegmentedControl } from '../../components/ui';
import { BackButton } from '../../components/ui/BackButton';
import { groupsApi } from '../../api/groups';
import { coachesApi } from '../../api/coaches';
import { athletesApi } from '../../api/athletes';
import { paymentSimpleApi } from '../../api/paymentSimple';
import { formatAge } from '../../utils/dateUtils';
import { formatBalance, PAYMENT_STATUS_LABELS, PAYMENT_STATUS_COLORS } from '../../types/payment_simple';
import type { Athlete } from '../../types/api';

export function GroupDetailScreen() {
  const { getCurrentScreen, popScreen, pushScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const queryClient = useQueryClient();

  const groupId = currentScreen.params?.groupId as string | undefined;

  // UI State
  const [activeTab, setActiveTab] = useState<'athletes' | 'payments'>('athletes');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Form State
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [selectedCoachIds, setSelectedCoachIds] = useState<Set<string>>(new Set());

  // Data Queries
  const { data: group, isLoading: isLoadingGroup } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => groupsApi.getById(groupId!),
    enabled: !!groupId,
  });

  const { data: groupCoaches, isLoading: isLoadingCoaches } = useQuery({
    queryKey: ['group-coaches', groupId],
    queryFn: () => coachesApi.getByGroupId(groupId!),
    enabled: !!groupId,
  });

  const { data: allCoaches } = useQuery({
    queryKey: ['coaches', currentAssistantId],
    queryFn: () => coachesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId && isEditing,
  });

  const { data: allAthletes } = useQuery({
    queryKey: ['athletes', currentAssistantId],
    queryFn: () => athletesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  const groupAthletes = useMemo(() => {
    if (!allAthletes || !groupId) return [];
    return allAthletes.filter((athlete: Athlete) => athlete.group_id === groupId);
  }, [allAthletes, groupId]);

  // Payment queries
  const { data: paymentConfig, error: paymentConfigError, status: paymentConfigStatus } = useQuery({
    queryKey: ['group-payment-config', groupId],
    queryFn: async () => {
      console.log('🔵 [PaymentConfig] Fetching config for group:', groupId);
      try {
        const result = await paymentSimpleApi.getGroupConfig(groupId!);
        console.log('✅ [PaymentConfig] Success:', result);
        return result;
      } catch (error: any) {
        console.log('❌ [PaymentConfig] Error:', error?.response?.status, error?.message);
        throw error;
      }
    },
    enabled: !!groupId && activeTab === 'payments',
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  const { data: balances } = useQuery({
    queryKey: ['group-balances', groupId],
    queryFn: () => paymentSimpleApi.getGroupBalances(groupId!),
    enabled: !!groupId && !!paymentConfig && activeTab === 'payments',
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  // Debug logging
  console.log('🎯 [GroupDetail] Render:', {
    groupId,
    activeTab,
    paymentConfigStatus,
    hasPaymentConfig: !!paymentConfig,
    paymentConfigError: paymentConfigError?.toString(),
  });

  // Initialize form data
  useEffect(() => {
    if (group && formData.name === '') {
      setFormData({ name: group.name, description: group.description || '' });
    }
  }, [group]);

  useEffect(() => {
    if (groupCoaches && selectedCoachIds.size === 0) {
      setSelectedCoachIds(new Set(groupCoaches.map((c) => c.id)));
    }
  }, [groupCoaches]);

  // Mutations
  const updateMutation = useMutation({
    mutationFn: async () => {
      await groupsApi.update(groupId!, {
        name: formData.name,
        description: formData.description || undefined,
      });
      const currentCoachIds = new Set(groupCoaches?.map((c) => c.id) || []);
      for (const coachId of currentCoachIds) {
        if (!selectedCoachIds.has(coachId)) {
          await coachesApi.removeFromGroup(coachId, groupId!);
        }
      }
      for (const coachId of selectedCoachIds) {
        if (!currentCoachIds.has(coachId)) {
          await coachesApi.assignToGroup(coachId, { group_id: groupId! });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['group-coaches', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groups', currentAssistantId] });
      setIsEditing(false);
    },
    onError: (error) => {
      console.error('Failed to update group:', error);
      alert('Не удалось обновить группу. Попробуйте еще раз.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => groupsApi.delete(groupId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups', currentAssistantId] });
      popScreen();
    },
    onError: (error) => {
      console.error('Failed to delete group:', error);
      alert('Не удалось удалить группу. Попробуйте еще раз.');
    },
  });

  // Handlers
  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('Название группы обязательно');
      return;
    }
    updateMutation.mutate();
  };

  const handleDelete = () => {
    setIsMenuOpen(false);
    if (confirm(`Вы уверены, что хотите удалить группу "${group?.name}"?`)) {
      deleteMutation.mutate();
    }
  };

  const toggleCoach = (coachId: string) => {
    const newSet = new Set(selectedCoachIds);
    if (newSet.has(coachId)) newSet.delete(coachId);
    else newSet.add(coachId);
    setSelectedCoachIds(newSet);
  };

  const handleBack = () => {
    if (isEditing) setIsEditing(false);
    else popScreen();
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    marginBottom: '8px',
  };

  // Loading
  if (isLoadingGroup || isLoadingCoaches) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    );
  }

  // Not found
  if (!group) {
    return (
      <div className="p-4">
        <Card>
          <p className="text-center py-8" style={{ color: 'var(--text-hint)' }}>
            Группа не найдена
          </p>
        </Card>
      </div>
    );
  }

  // Edit Mode
  if (isEditing) {
    return (
      <div className="min-h-screen pb-24" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-2">
            <BackButton onClick={handleBack} />
          </div>

          <Card>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Редактировать группу
            </h2>
            <div className="space-y-4">
              <div>
                <label style={labelStyle}>Название *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Например: Группа начинающих"
                  style={inputStyle}
                  disabled={updateMutation.isPending}
                />
              </div>

              <div>
                <label style={labelStyle}>Описание</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Описание группы..."
                  rows={3}
                  style={{ ...inputStyle, resize: 'none' }}
                  disabled={updateMutation.isPending}
                />
              </div>

              <div>
                <label style={labelStyle}>Тренеры</label>
                {allCoaches && allCoaches.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto rounded-xl p-3" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                    {allCoaches.map((coach) => (
                      <label key={coach.id} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer active:opacity-60">
                        <input
                          type="checkbox"
                          checked={selectedCoachIds.has(coach.id)}
                          onChange={() => toggleCoach(coach.id)}
                          disabled={updateMutation.isPending}
                          className="w-5 h-5"
                        />
                        <span className="text-[17px]" style={{ color: 'var(--text-primary)' }}>
                          {coach.name}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-[15px] italic" style={{ color: 'var(--text-hint)' }}>
                    Нет доступных тренеров
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleSave}
                  disabled={updateMutation.isPending || !formData.name.trim()}
                  loading={updateMutation.isPending}
                  fullWidth
                >
                  Сохранить
                </Button>
                <Button
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({ name: group.name, description: group.description || '' });
                    setSelectedCoachIds(new Set(groupCoaches?.map((c) => c.id) || []));
                  }}
                  disabled={updateMutation.isPending}
                  variant="secondary"
                >
                  Отмена
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // View Mode with Tabs
  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <BackButton onClick={handleBack} />
        <button
          onClick={() => setIsMenuOpen(true)}
          className="p-2 rounded-xl active:opacity-60 transition-opacity"
          style={{ backgroundColor: 'var(--bg-secondary)' }}
        >
          <MoreVertical size={20} style={{ color: 'var(--text-primary)' }} />
        </button>
      </div>

      {/* Tabs */}
      <div className="px-4 pb-4">
        <SegmentedControl
          segments={[
            { value: 'athletes', label: 'Спортсмены' },
            { value: 'payments', label: 'Оплаты' },
          ]}
          value={activeTab}
          onChange={(value) => setActiveTab(value as 'athletes' | 'payments')}
        />
      </div>

      {/* Content */}
      <div className="px-4 space-y-4">
        {activeTab === 'athletes' ? (
          <>
            {/* Group Info Card */}
            <Card>
              <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {group.name}
              </h2>

              {group.description && (
                <p className="text-[15px] mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {group.description}
                </p>
              )}

              {/* Coaches */}
              <div className="pt-3" style={{ borderTop: '1px solid var(--separator)' }}>
                <p className="text-[13px] font-semibold mb-2" style={{ color: 'var(--text-hint)' }}>
                  Тренеры
                </p>
                {groupCoaches && groupCoaches.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {groupCoaches.map((coach) => (
                      <span
                        key={coach.id}
                        className="text-[13px] px-3 py-1.5 rounded-full font-medium"
                        style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent)' }}
                      >
                        {coach.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-[15px] italic" style={{ color: 'var(--text-hint)' }}>
                    Нет закрепленных тренеров
                  </p>
                )}
              </div>
            </Card>

            {/* Athletes List */}
            <Card>
              <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                Спортсмены ({groupAthletes.length})
              </h3>

              {groupAthletes.length > 0 ? (
                <div className="space-y-2">
                  {groupAthletes.map((athlete) => (
                    <div
                      key={athlete.id}
                      onClick={() => pushScreen('athlete-detail', { athleteId: athlete.id, fromGroup: groupId })}
                      className="flex items-center justify-between p-3 rounded-xl cursor-pointer active:opacity-60 transition-opacity"
                      style={{ backgroundColor: 'var(--bg-secondary)' }}
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="text-[17px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                          {athlete.name}
                        </h4>
                        {athlete.phone && (
                          <p className="text-[13px] truncate" style={{ color: 'var(--text-hint)' }}>
                            {athlete.phone}
                          </p>
                        )}
                      </div>
                      {athlete.date_of_birth && (
                        <span className="text-[15px] ml-3 flex-shrink-0" style={{ color: 'var(--text-hint)' }}>
                          {formatAge(athlete.date_of_birth)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[15px] text-center py-8" style={{ color: 'var(--text-hint)' }}>
                  В группе пока нет спортсменов
                </p>
              )}
            </Card>
          </>
        ) : paymentConfig && paymentConfigStatus === 'success' ? (
          <>
            {/* Кнопки управления */}
            <div className="flex gap-2 mb-4">
              <Button
                variant="secondary"
                onClick={() =>
                  pushScreen('payment-overrides', { groupId: group.id, groupName: group.name })
                }
                fullWidth
              >
                Льготы
              </Button>
              <Button
                variant="secondary"
                onClick={() =>
                  pushScreen('payment-settings', { groupId: group.id, groupName: group.name })
                }
                fullWidth
              >
                Настройки
              </Button>
            </div>

            {/* Балансы спортсменов */}
            <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
              Балансы спортсменов
            </h3>

            {balances && balances.length > 0 ? (
              <Card>
                <div className="space-y-1">
                  {balances.map((balance) => {
                    const athlete = groupAthletes.find((a) => a.id === balance.athlete_id);
                    return (
                      <div
                        key={balance.athlete_id}
                        className="flex items-center justify-between py-3 px-2 rounded-lg cursor-pointer active:opacity-60"
                        style={{ borderBottom: '1px solid var(--separator)' }}
                        onClick={() => pushScreen('athlete-detail', { athleteId: balance.athlete_id })}
                      >
                        <div className="flex-1">
                          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            {athlete?.name || 'Спортсмен'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className="text-sm font-medium"
                            style={{ color: balance.balance >= 0 ? 'var(--text-primary)' : 'var(--danger)' }}
                          >
                            {formatBalance(balance.balance)}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${PAYMENT_STATUS_COLORS[balance.status]}`}>
                            {PAYMENT_STATUS_LABELS[balance.status]}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            ) : (
              <Card>
                <p className="text-[15px] text-center py-8" style={{ color: 'var(--text-hint)' }}>
                  Нет данных о балансах. Запишите первый платёж.
                </p>
              </Card>
            )}
          </>
        ) : (
          <Card>
            <div className="py-8 text-center">
              <p className="text-[17px] mb-2" style={{ color: 'var(--text-primary)' }}>
                Оплата не настроена
              </p>
              <p className="text-[15px] mb-4" style={{ color: 'var(--text-hint)' }}>
                Настройте базовую стоимость и день списания
              </p>
              <Button
                onClick={() =>
                  pushScreen('payment-settings', { groupId: group.id, groupName: group.name })
                }
              >
                Настроить оплату
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Actions Menu Bottom Sheet */}
      <BottomSheet isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} height="30%">
        <div className="py-4 space-y-2">
          <button
            onClick={() => {
              setIsMenuOpen(false);
              setIsEditing(true);
            }}
            className="w-full flex items-center gap-3 p-4 rounded-xl active:opacity-60 transition-opacity"
            style={{ backgroundColor: 'var(--bg-secondary)' }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'var(--accent-light)' }}
            >
              <Edit2 size={20} style={{ color: 'var(--accent)' }} />
            </div>
            <span className="text-[17px] font-medium" style={{ color: 'var(--text-primary)' }}>
              Редактировать
            </span>
          </button>

          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="w-full flex items-center gap-3 p-4 rounded-xl active:opacity-60 transition-opacity"
            style={{ backgroundColor: 'var(--bg-secondary)' }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255, 59, 48, 0.1)' }}
            >
              <Trash2 size={20} style={{ color: 'var(--danger)' }} />
            </div>
            <span className="text-[17px] font-medium" style={{ color: 'var(--danger)' }}>
              {deleteMutation.isPending ? 'Удаление...' : 'Удалить группу'}
            </span>
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}
